import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DoorOpen, X, CheckCircle2, XCircle } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';

/** API-1: aggiornamento del pannello accessi. Con controllo accessi spento si ricontrolla raramente. */
const POLL_MS = 5000;
const POLL_DISABLED_MS = 60000;
const POPUP_MS = 6000;
const MAX_POPUPS = 4;

function timeOf(scannedAt) {
    const m = /\d{2}:\d{2}/.exec(scannedAt || '');
    return m ? m[0] : '';
}

/**
 * Pannello "Accessi di oggi" in alto a destra su ogni pagina del gestionale (gestore e trainer).
 * Popup per ogni nuovo ingresso, verde se consentito e rosso se rifiutato (con motivo e orario).
 * Non compare se la palestra non ha il controllo accessi acceso.
 */
export default function AccessLivePanel() {
    const { t } = useTranslation();
    const { getAuthenticatedApi, isGym, isTrainer } = useAuth();
    const canSee = isGym() || isTrainer();

    const [enabled, setEnabled] = useState(false);
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [popups, setPopups] = useState([]);
    const lastIdRef = useRef(null);
    const loadedRef = useRef(false);

    const reasonLabel = useCallback(
        (reason) => t(`gym.accessControl.reasons.${reason}`, reason),
        [t]
    );

    const poll = useCallback(async () => {
        const api = getAuthenticatedApi();
        const since = lastIdRef.current;
        const res = await api.get(`/gym_access.php?action=today${since ? `&since_id=${since}` : ''}`);
        if (!res.data?.success || !res.data.enabled) {
            setEnabled(false);
            return false;
        }
        setEnabled(true);
        const fresh = Array.isArray(res.data.items) ? res.data.items : [];
        if (fresh.length > 0) {
            lastIdRef.current = Math.max(...fresh.map((i) => i.id), since || 0);
            setItems((prev) => (since ? [...fresh, ...prev] : fresh));
            // al primo caricamento niente popup: solo per gli ingressi nuovi
            if (loadedRef.current) {
                const now = Date.now();
                setPopups((prev) => [...fresh.slice(0, MAX_POPUPS).reverse().map((i) => ({ ...i, shownAt: now })), ...prev].slice(0, MAX_POPUPS));
            }
        }
        loadedRef.current = true;
        return true;
    }, [getAuthenticatedApi]);

    useEffect(() => {
        if (!canSee) return undefined;
        let timer;
        let stopped = false;
        const loop = async () => {
            let on = false;
            try {
                if (document.visibilityState !== 'hidden') on = await poll();
                else on = true;
            } catch {
                on = true;
            }
            if (!stopped) timer = setTimeout(loop, on ? POLL_MS : POLL_DISABLED_MS);
        };
        loop();
        return () => {
            stopped = true;
            clearTimeout(timer);
        };
    }, [canSee, poll]);

    useEffect(() => {
        if (popups.length === 0) return undefined;
        const timer = setInterval(() => {
            setPopups((prev) => prev.filter((p) => Date.now() - p.shownAt < POPUP_MS));
        }, 500);
        return () => clearInterval(timer);
    }, [popups.length]);

    if (!canSee || !enabled) return null;

    const allowedCount = new Set(items.filter((i) => i.allowed).map((i) => i.user_id)).size;
    const deniedCount = items.filter((i) => !i.allowed).length;

    const describe = (i) => {
        if (!i.allowed) return `${t('gym.accessControl.denied', 'Rifiutato')}: ${reasonLabel(i.reason)}`;
        if (i.entries_remaining != null) {
            return t('gym.accessControl.allowedEntries', 'Consentito · {{count}} ingressi rimasti', { count: i.entries_remaining });
        }
        return t('gym.accessControl.allowed', 'Consentito');
    };

    return (
        <div
            className="relative z-40 flex shrink-0 justify-end border-b border-gray-200/70 px-4 py-1.5 dark:border-white/10"
            data-testid="access-live-panel"
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 shadow-sm hover:bg-gray-50 dark:border-white/10 dark:bg-gray-900 dark:text-white dark:hover:bg-gray-800"
            >
                <DoorOpen size={16} aria-hidden />
                {t('gym.accessControl.todayButton', 'Accessi oggi')}
                <span className="rounded-full bg-emerald-100 px-2 text-xs text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200" data-testid="access-count">
                    {allowedCount}
                </span>
                {deniedCount > 0 && (
                    <span className="rounded-full bg-red-100 px-2 text-xs text-red-800 dark:bg-red-900/50 dark:text-red-200" data-testid="access-denied-count">
                        {deniedCount}
                    </span>
                )}
            </button>

            <div className="absolute right-4 top-full mt-2 flex flex-col items-end gap-2">
            {open && (
                <div className="w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-white shadow-lg dark:border-white/10 dark:bg-gray-900" data-testid="access-list">
                    <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2 dark:border-white/10">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                            {t('gym.accessControl.todayTitle', 'Accessi di oggi')}
                        </span>
                        <button type="button" onClick={() => setOpen(false)} aria-label={t('common.close', 'Chiudi')} className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:text-white/60 dark:hover:bg-white/10">
                            <X size={14} />
                        </button>
                    </div>
                    {items.length === 0 ? (
                        <p className="px-3 py-4 text-sm text-gray-500 dark:text-white/60">{t('gym.accessControl.empty', 'Nessun accesso oggi')}</p>
                    ) : (
                        <ul className="max-h-96 divide-y divide-gray-100 overflow-y-auto dark:divide-white/10">
                            {items.map((i) => (
                                <li key={i.id} className="flex items-start gap-2 px-3 py-2 text-sm" data-testid="access-row">
                                    {i.allowed
                                        ? <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
                                        : <XCircle size={16} className="mt-0.5 shrink-0 text-red-600 dark:text-red-400" aria-hidden />}
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate font-medium text-gray-900 dark:text-white">
                                            {i.user_name || t('gym.accessControl.unknownUser', 'QR non riconosciuto')}
                                        </p>
                                        <p className={`text-xs ${i.allowed ? 'text-gray-500 dark:text-white/60' : 'text-red-700 dark:text-red-300'}`}>
                                            {describe(i)}
                                        </p>
                                    </div>
                                    <span className="shrink-0 text-xs tabular-nums text-gray-500 dark:text-white/55">{timeOf(i.scanned_at)}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            {popups.map((p) => (
                <div
                    key={`popup-${p.id}`}
                    role="status"
                    data-testid={p.allowed ? 'access-popup-allowed' : 'access-popup-denied'}
                    className={`flex w-80 max-w-[calc(100vw-2rem)] items-start gap-3 rounded-xl border p-3 shadow-lg ${
                        p.allowed
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-500/40 dark:bg-emerald-950 dark:text-emerald-100'
                            : 'border-red-200 bg-red-50 text-red-900 dark:border-red-500/40 dark:bg-red-950 dark:text-red-100'
                    }`}
                >
                    {p.allowed ? <CheckCircle2 size={22} className="shrink-0" aria-hidden /> : <XCircle size={22} className="shrink-0" aria-hidden />}
                    <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold">{p.user_name || t('gym.accessControl.unknownUser', 'QR non riconosciuto')}</p>
                        <p className="text-sm">{describe(p)}</p>
                        <p className="text-xs opacity-75">{timeOf(p.scanned_at)}</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setPopups((prev) => prev.filter((x) => x.id !== p.id))}
                        aria-label={t('common.close', 'Chiudi')}
                        className="rounded p-1 opacity-70 hover:opacity-100"
                    >
                        <X size={14} />
                    </button>
                </div>
            ))}
            </div>
        </div>
    );
}
